import styled, { css, keyframes } from "styled-components"

const slideUp = keyframes`
   from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
   }
   to {
      opacity: 1;
      transform: translateY(0) scale(1);
   }
`

const fadeIn = keyframes`
   from {
      opacity: 0;
      transform: translateY(8px);
   }
   to {
      opacity: 1;
      transform: translateY(0);
   }
`

const pulse = keyframes`
   0% {
      box-shadow: 0 0 0 0 rgba(226, 118, 38, 0.4);
   }
   70% {
      box-shadow: 0 0 0 12px rgba(226, 118, 38, 0);
   }
   100% {
      box-shadow: 0 0 0 0 rgba(226, 118, 38, 0);
   }
`

const bounce = keyframes`
   0%, 60%, 100% {
      transform: translateY(0);
   }
   30% {
      transform: translateY(-4px);
   }
`

// FAB Button
export const FABButton = styled.button`
   position: fixed;
   bottom: 24px;
   right: 24px;
   width: 56px;
   height: 56px;
   border-radius: 50%;
   background: #e27626;
   border: none;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: center;
   color: #fff;
   font-size: 24px;
   box-shadow: 0 4px 16px rgba(226, 118, 38, 0.3);
   transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
   z-index: 10000;

   &:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 20px rgba(226, 118, 38, 0.4);
   }

   &:active {
      transform: scale(0.95);
   }

   ${({ $hasUnread }) =>
      $hasUnread &&
      css`
         animation: ${pulse} 2s infinite;
      `}
`

export const UnreadBadge = styled.span`
   position: absolute;
   top: -4px;
   right: -4px;
   background: #ef4444;
   color: #fff;
   font-size: 11px;
   font-weight: 700;
   min-width: 20px;
   height: 20px;
   border-radius: 10px;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 0 5px;
   border: 2px solid #fff;
`

// Panel Container
export const PanelContainer = styled.div`
   position: fixed;
   bottom: 92px;
   right: 24px;
   width: 380px;
   height: 560px;
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
   display: flex;
   flex-direction: column;
   overflow: hidden;
   z-index: 10000;
   animation: ${slideUp} 0.25s ease-out;

   @media (max-width: 480px) {
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      border-radius: 0;
   }
`

// Header
export const HeaderContainer = styled.div`
   background: #101d37;
   padding: 16px 20px;
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-shrink: 0;
`

export const HeaderLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`

export const HeaderTitle = styled.h3`
   color: #fff;
   font-size: 15px;
   font-weight: 600;
   margin: 0;
   line-height: 1;
`

export const HeaderSubtitle = styled.span`
   color: rgba(255, 255, 255, 0.6);
   font-size: 12px;
   display: flex;
   align-items: center;
   gap: 5px;
`

export const OnlineDot = styled.span`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   background: #22c55e;
   display: inline-block;
`

export const HeaderActions = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`

export const HeaderButton = styled.button`
   background: none;
   border: none;
   color: rgba(255, 255, 255, 0.7);
   cursor: pointer;
   padding: 6px;
   border-radius: 6px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 18px;
   transition:
      color 0.15s ease,
      background 0.15s ease;

   &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.1);
   }
`

// Message List
export const MessageList = styled.div`
   flex: 1;
   overflow-y: auto;
   padding: 16px;
   display: flex;
   flex-direction: column;
   gap: 12px;

   &::-webkit-scrollbar {
      width: 4px;
   }

   &::-webkit-scrollbar-track {
      background: transparent;
   }

   &::-webkit-scrollbar-thumb {
      background: #d0d5dd;
      border-radius: 2px;
   }
`

// Message Bubble
export const MessageBubble = styled.div`
   max-width: 85%;
   padding: 10px 14px;
   font-size: 14px;
   line-height: 1.5;
   word-wrap: break-word;
   animation: ${fadeIn} 0.2s ease-out;

   ${({ $role }) =>
      $role === "user"
         ? css`
              background: #e27626;
              color: #fff;
              border-radius: 14px 14px 4px 14px;
              align-self: flex-end;
              white-space: pre-wrap;
           `
         : css`
              background: #f2f4f7;
              color: #1d2939;
              border-radius: 14px 14px 14px 4px;
              align-self: flex-start;
           `}

   /* Markdown styling for assistant messages */
   p {
      margin: 0 0 8px 0;

      &:last-child {
         margin-bottom: 0;
      }
   }

   strong {
      font-weight: 600;
   }

   ul,
   ol {
      margin: 4px 0 8px 0;
      padding-left: 18px;

      &:last-child {
         margin-bottom: 0;
      }
   }

   li {
      margin-bottom: 4px;

      &:last-child {
         margin-bottom: 0;
      }
   }

   code {
      background: rgba(0, 0, 0, 0.06);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 13px;
   }

   h3,
   h4 {
      font-size: 14px;
      font-weight: 600;
      margin: 8px 0 4px 0;

      &:first-child {
         margin-top: 0;
      }
   }
`

// Input Area
export const InputContainer = styled.div`
   padding: 12px 16px;
   border-top: 1px solid #e4e7ec;
   display: flex;
   align-items: flex-end;
   gap: 8px;
   background: #fff;
   flex-shrink: 0;
`

export const TextArea = styled.textarea`
   flex: 1;
   border: 1px solid #d0d5dd;
   border-radius: 12px;
   padding: 10px 14px;
   font-size: 14px;
   line-height: 1.4;
   resize: none;
   outline: none;
   max-height: 100px;
   font-family: inherit;
   background: #f9fafb;
   color: #1d2939;
   transition: border-color 0.15s ease;

   &:focus {
      border-color: #e27626;
      background: #fff;
   }

   &::placeholder {
      color: #98a2b3;
   }
`

export const SendButton = styled.button`
   width: 40px;
   height: 40px;
   border-radius: 10px;
   border: none;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 18px;
   flex-shrink: 0;
   transition:
      background 0.15s ease,
      transform 0.1s ease;

   ${({ $isStreaming }) =>
      $isStreaming
         ? css`
              background: #ef4444;
              color: #fff;

              &:hover {
                 background: #dc2626;
              }
           `
         : css`
              background: #e27626;
              color: #fff;

              &:hover {
                 background: #c9651e;
              }

              &:disabled {
                 background: #d0d5dd;
                 cursor: not-allowed;
              }
           `}

   &:active {
      transform: scale(0.93);
   }
`

// Typing Indicator
export const TypingContainer = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   padding: 10px 14px;
   background: #f2f4f7;
   border-radius: 14px 14px 14px 4px;
   align-self: flex-start;
   max-width: 60px;
`

export const TypingDot = styled.span`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: #98a2b3;
   animation: ${bounce} 1.2s ease-in-out infinite;
   animation-delay: ${({ $delay }) => $delay || "0s"};
`

// Trip Summary Card
export const SummaryCard = styled.div`
   background: #fff;
   border: 1px solid #e4e7ec;
   border-radius: 12px;
   padding: 16px;
   margin-top: 8px;
   align-self: flex-start;
   max-width: 90%;
   animation: ${fadeIn} 0.3s ease-out;
`

export const SummaryTitle = styled.h4`
   font-size: 14px;
   font-weight: 600;
   color: #101d37;
   margin: 0 0 12px 0;
   display: flex;
   align-items: center;
   gap: 6px;
`

export const SummaryGrid = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
   margin-bottom: 14px;
`

export const SummaryChip = styled.div`
   background: #f2f4f7;
   border-radius: 8px;
   padding: 6px 10px;
   font-size: 12px;
   color: #344054;

   span {
      color: #667085;
      font-size: 11px;
      display: block;
      margin-bottom: 2px;
   }
`

export const FillFormButton = styled.button`
   width: 100%;
   background: #e27626;
   color: #fff;
   border: none;
   border-radius: 10px;
   padding: 10px 16px;
   font-size: 14px;
   font-weight: 600;
   cursor: pointer;
   transition: background 0.15s ease;
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 6px;

   &:hover {
      background: #c9651e;
   }

   &:active {
      transform: scale(0.98);
   }
`

// Error Message
export const ErrorMessage = styled.div`
   background: #fef3f2;
   color: #b42318;
   padding: 8px 12px;
   border-radius: 8px;
   font-size: 13px;
   align-self: center;
   text-align: center;
   max-width: 90%;
`
